/**
 * Evidence Readiness Assessment (Discovery-only, informational)
 *
 * IMPORTANT: This module is INDEPENDENT of the ML2 / CES / PGS / CMS scoring engine.
 * It reads answers to the 28 evidence_readiness questions and computes a separate
 * informational readiness signal. It MUST NOT influence:
 *   - Weighted ML2 Score, CES, PGS, CMS
 *   - Domain maturity scores
 *   - Gating controls / submission decision
 *   - CSQ mapping, DISP gap analysis, hybrid report
 *
 * The Accelerator remains responsible for detailed evidence collection, upload,
 * validation, mapping and management. This is a Discovery high-level check only.
 */

// Stable Accelerator-ready IDs. Field IDs in question-config.js MUST match these.
export const EVIDENCE_GROUPS = [
  // Governance (7)
  { id: 'GOV-EV-01', domain: 'governance', name: 'Security Policies',
    tooltip: 'Examples include information security policy, acceptable use policy, access control policy, and cryptographic policy.' },
  { id: 'GOV-EV-02', domain: 'governance', name: 'Risk Management Documentation',
    tooltip: 'Examples include risk register, risk assessments, treatment plans, and residual risk acceptance records.' },
  { id: 'GOV-EV-03', domain: 'governance', name: 'Compliance & Assurance Documentation',
    tooltip: 'Examples include audit reports, compliance attestations, control matrices, and third-party assurance reviews.' },
  { id: 'GOV-EV-04', domain: 'governance', name: 'DISP Registration Scope Documentation',
    tooltip: 'Examples include DISP membership records, scope statements, facility lists, and personnel scope definitions.' },
  { id: 'GOV-EV-05', domain: 'governance', name: 'Annual Security Report (ASR) Documentation',
    tooltip: 'Examples include prior ASR submissions, evidence packs, and Defence correspondence relating to ASR.' },
  { id: 'GOV-EV-06', domain: 'governance', name: 'Ownership & Control Documentation',
    tooltip: 'Examples include beneficial ownership records, shareholder registers, board composition, and control disclosures.' },
  { id: 'GOV-EV-07', domain: 'governance', name: 'FOCI Assessment & Mitigation Documentation',
    tooltip: 'Examples include FOCI disclosures, mitigation plans, foreign influence assessments, and Defence responses.' },

  // Personnel Security (6)
  { id: 'PER-EV-01', domain: 'personnel_security', name: 'Personnel Screening Records',
    tooltip: 'Examples include pre-employment checks, identity verification, reference checks, and screening decisions.' },
  { id: 'PER-EV-02', domain: 'personnel_security', name: 'Baseline Vetting Records',
    tooltip: 'Examples include AGSVA baseline records, sponsorship documents, and personnel security file references.' },
  { id: 'PER-EV-03', domain: 'personnel_security', name: 'Security Clearance Management Records',
    tooltip: 'Examples include clearance registers, renewal tracking, briefings/debriefings, and access-authorisation records.' },
  { id: 'PER-EV-04', domain: 'personnel_security', name: 'Security Awareness & Insider Threat Records',
    tooltip: 'Examples include training completion records, awareness campaign artefacts, and insider-threat program documentation.' },
  { id: 'PER-EV-05', domain: 'personnel_security', name: 'Personnel Offboarding Records',
    tooltip: 'Examples include exit checklists, access revocation confirmations, asset returns, and clearance debrief records.' },
  { id: 'PER-EV-06', domain: 'personnel_security', name: 'Contractor & Third-Party Security Records',
    tooltip: 'Examples include contractor screening evidence, third-party agreements, NDAs, and sub-contractor DISP status.' },

  // Physical Security (4)
  { id: 'PHY-EV-01', domain: 'physical_security', name: 'Physical Access Control Documentation',
    tooltip: 'Examples include zone plans, access-control policies, badge issuance records, and access audit logs.' },
  { id: 'PHY-EV-02', domain: 'physical_security', name: 'Visitor Management Documentation',
    tooltip: 'Examples include visitor logs, escort policies, sign-in system records, and visitor briefings.' },
  { id: 'PHY-EV-03', domain: 'physical_security', name: 'Facility Security Documentation',
    tooltip: 'Examples include facility security plans, T4/Zone certifications, and secure area authorisation records.' },
  { id: 'PHY-EV-04', domain: 'physical_security', name: 'Physical Monitoring & Alarm Documentation',
    tooltip: 'Examples include CCTV coverage plans, alarm system records, monitoring rosters, and incident response records.' },

  // ICT / Cyber Security (11)
  { id: 'ICT-EV-01', domain: 'ict_cyber', name: 'Identity & MFA Evidence',
    tooltip: 'Examples include MFA policies, identity platform configuration, conditional access policies, authentication logs and MFA coverage reports.' },
  { id: 'ICT-EV-02', domain: 'ict_cyber', name: 'Privileged Access Management Evidence',
    tooltip: 'Examples include PAM tool configuration, privileged session recordings, JIT elevation logs, and privileged-account inventory.' },
  { id: 'ICT-EV-03', domain: 'ict_cyber', name: 'Endpoint Management Evidence',
    tooltip: 'Examples include MDM/UEM configuration, device compliance reports, endpoint enrolment records, and baseline configurations.' },
  { id: 'ICT-EV-04', domain: 'ict_cyber', name: 'Device Security & Encryption Evidence',
    tooltip: 'Examples include disk encryption reports, EDR coverage exports, secure-configuration baselines, and device-hardening records.' },
  { id: 'ICT-EV-05', domain: 'ict_cyber', name: 'Application Control Evidence',
    tooltip: 'Examples include allow-listing policies, execution-control tool configuration, unauthorised-execution logs, and exception registers.' },
  { id: 'ICT-EV-06', domain: 'ict_cyber', name: 'Application Hardening Evidence',
    tooltip: 'Examples include browser and Office hardening policies, script/macro control configuration, and PDF/HTML restriction settings.' },
  { id: 'ICT-EV-07', domain: 'ict_cyber', name: 'Patch Management Evidence',
    tooltip: 'Examples include patch policies, patch compliance reports, remediation SLAs, and unpatched-system exception records.' },
  { id: 'ICT-EV-08', domain: 'ict_cyber', name: 'Vulnerability Management Evidence',
    tooltip: 'Examples include vulnerability scanner outputs, remediation tickets, CVSS-based prioritisation records, and re-scan verifications.' },
  { id: 'ICT-EV-09', domain: 'ict_cyber', name: 'Security Monitoring & Logging Evidence',
    tooltip: 'Examples include SIEM configuration, log retention policies, detection rule inventory, and alert triage records.' },
  { id: 'ICT-EV-10', domain: 'ict_cyber', name: 'Incident Response Evidence',
    tooltip: 'Examples include IR plans, playbooks, post-incident reviews, tabletop exercise records, and IR contact lists.' },
  { id: 'ICT-EV-11', domain: 'ict_cyber', name: 'Backup & Recovery Evidence',
    tooltip: 'Examples include backup policies, restoration test logs, immutability configuration, and RPO/RTO validation records.' },
];

export const EVIDENCE_DOMAIN_LABELS = {
  governance: 'Governance',
  personnel_security: 'Personnel Security',
  physical_security: 'Physical Security',
  ict_cyber: 'ICT / Cyber Security',
};

export const EVIDENCE_DOMAIN_ORDER = ['governance', 'personnel_security', 'physical_security', 'ict_cyber'];

// Convert a stable Accelerator ID into a question field_id used by the question-config
// engine (lowercase, hyphens -> underscores). Kept centralised so ID mapping cannot drift.
export const evidenceGroupFieldId = (groupId) => groupId.toLowerCase().replace(/-/g, '_');

const bandFor = (percent) => {
  if (percent >= 80) return { label: 'Strong', color: 'emerald' };
  if (percent >= 60) return { label: 'Moderate', color: 'sky' };
  if (percent >= 40) return { label: 'Developing', color: 'amber' };
  return { label: 'Weak', color: 'rose' };
};

const responseFromScore = (score) => {
  if (score === 2) return 'Available';
  if (score === 1) return 'Partially Available';
  if (score === 0) return 'Not Available';
  return null;
};

/**
 * Compute Evidence Readiness signal from raw answers.
 * Returns { assessed: false } when the user has never answered any of the 28 groups
 * (so historical assessments render as "Not Assessed" instead of 0%).
 */
export const computeEvidenceReadiness = (answers = {}) => {
  const perGroup = EVIDENCE_GROUPS.map(group => {
    const fieldId = evidenceGroupFieldId(group.id);
    const payload = answers[fieldId];
    const rawScore = payload && typeof payload.score === 'number' ? payload.score : null;
    return {
      ...group,
      field_id: fieldId,
      response: responseFromScore(rawScore),
      score: rawScore,
      answered: rawScore !== null,
    };
  });

  const answeredCount = perGroup.filter(g => g.answered).length;
  if (answeredCount === 0) {
    return {
      assessed: false,
      overall_percent: null,
      band: null,
      counts: { available: 0, partial: 0, missing: 0, not_answered: EVIDENCE_GROUPS.length },
      per_domain: EVIDENCE_DOMAIN_ORDER.map(d => ({
        domain: d,
        label: EVIDENCE_DOMAIN_LABELS[d],
        percent: null,
        band: null,
        counts: { available: 0, partial: 0, missing: 0, not_answered: 0 },
      })),
      groups: perGroup,
      gaps: { by_domain: [] },
      narrative: 'Evidence Readiness has not been assessed for this engagement.',
    };
  }

  // Overall
  // Every unanswered group counts as 0 points (Discovery cannot confirm evidence exists).
  const totalGroups = EVIDENCE_GROUPS.length;
  const achieved = perGroup.reduce((sum, g) => sum + (g.score ?? 0), 0);
  const possible = totalGroups * 2;
  const overallPercent = Math.round((achieved / possible) * 100);
  const overallBand = bandFor(overallPercent);

  const counts = perGroup.reduce((acc, g) => {
    if (g.score === 2) acc.available += 1;
    else if (g.score === 1) acc.partial += 1;
    else if (g.score === 0) acc.missing += 1;
    else acc.not_answered += 1;
    return acc;
  }, { available: 0, partial: 0, missing: 0, not_answered: 0 });

  // Per-domain
  const perDomain = EVIDENCE_DOMAIN_ORDER.map(domain => {
    const groups = perGroup.filter(g => g.domain === domain);
    const domainAnswered = groups.filter(g => g.answered);
    const domainAchieved = groups.reduce((s, g) => s + (g.score ?? 0), 0);
    const domainPossible = groups.length * 2;
    // Show "not assessed" for a domain only if the user hasn't answered ANY of its groups.
    if (domainAnswered.length === 0) {
      return {
        domain,
        label: EVIDENCE_DOMAIN_LABELS[domain],
        percent: null,
        band: null,
        counts: { available: 0, partial: 0, missing: 0, not_answered: groups.length },
      };
    }
    const percent = Math.round((domainAchieved / domainPossible) * 100);
    const domainCounts = groups.reduce((acc, g) => {
      if (g.score === 2) acc.available += 1;
      else if (g.score === 1) acc.partial += 1;
      else if (g.score === 0) acc.missing += 1;
      else acc.not_answered += 1;
      return acc;
    }, { available: 0, partial: 0, missing: 0, not_answered: 0 });
    return {
      domain,
      label: EVIDENCE_DOMAIN_LABELS[domain],
      percent,
      band: bandFor(percent),
      counts: domainCounts,
    };
  });

  // Gaps grouped by domain
  const gapsByDomain = EVIDENCE_DOMAIN_ORDER.map(domain => {
    const domainGroups = perGroup.filter(g => g.domain === domain);
    const notAvailable = domainGroups.filter(g => g.score === 0).map(g => g.name);
    const partiallyAvailable = domainGroups.filter(g => g.score === 1).map(g => g.name);
    return {
      domain,
      label: EVIDENCE_DOMAIN_LABELS[domain],
      not_available: notAvailable,
      partially_available: partiallyAvailable,
    };
  }).filter(d => d.not_available.length > 0 || d.partially_available.length > 0);

  // Narrative
  let narrative;
  if (overallPercent >= 80) {
    narrative = 'The organisation demonstrates strong reported evidence coverage. Supporting evidence appears to exist across the majority of DISP control areas. Note that Discovery has only confirmed the reported presence of evidence; detailed validation of sufficiency remains an Accelerator function.';
  } else if (overallPercent >= 60) {
    narrative = 'The organisation demonstrates moderate evidence readiness. Supporting evidence exists across several DISP control areas; however, gaps remain that may prevent the organisation from demonstrating consistent and defensible compliance during DISP assessment. Missing and partially available evidence should be collected, validated and mapped to applicable DISP requirements prior to submission.';
  } else if (overallPercent >= 40) {
    narrative = 'Evidence readiness is developing but incomplete. Significant categories of supporting evidence are missing or only partially available. In its current state, the organisation is unlikely to demonstrate defensible DISP compliance without structured evidence uplift.';
  } else {
    narrative = 'Evidence readiness is weak. The majority of expected evidence categories are either missing or only partially available. Without a structured evidence-collection and validation program, the organisation is not positioned to substantiate DISP posture during submission.';
  }

  return {
    assessed: true,
    overall_percent: overallPercent,
    band: overallBand,
    counts,
    per_domain: perDomain,
    groups: perGroup,
    gaps: { by_domain: gapsByDomain },
    narrative,
  };
};

export default computeEvidenceReadiness;
